import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import baseUrl from "../../baseUrl";
import logo3 from "../../logo3.png";
import {S3View} from "./S3View";
import styles from "./PublicView.module.css";
import {CropsView} from "../Reports/CropsView";
import {CrossView} from "../Reports/CrossView";
import {S14View} from "../Reports/S14View";
import {SaveToDevice} from "./SaveToDevice";
import {PushNotifications} from "./PushNotifications";
import {ConsentGate} from "./ConsentGate";
import {DocViewer} from "../common/DocViewer/DocViewer";
import {useForceLightTheme} from "../../contexts/ThemeContext";
import {readSeen, writeSeen, diffSettlements, baselineFor, scopeKey,
        readNotices, markNoticesSeen, unseenNotices} from "../../utils/publicSeen";

// Е10 — публична страница /p/:token (ИЗВЪН вход и извън AppLayout). Заявките са
// с обикновен fetch (без Bearer). Неутрално заглавие „Справка".

// Ъ8: първият ред на съобщението, съкратен — за лентата с известия.
const кратко = (текст, n = 80) => {
    const ред = String(текст || '').split('\n')[0].trim();
    return ред.length > n ? `${ред.slice(0, n - 1)}…` : ред;
};

// Ц1: съставът на линка. Отговор от по-стар сървър (без `companies`) се свежда
// до едната фирма, за да не зависи страницата от реда на деплоя.
const фирмиОт = (info) => (info.companies && info.companies.length > 0)
    ? info.companies
    : [{bullstat: info.company.bullstat,
        company_name: info.company.company_name,
        years: info.years || []}];
export const PublicView = () => {
    useForceLightTheme();   // страницата е за външен посетител — винаги светла

    const {token} = useParams();
    const [status, setStatus] = useState("loading");   // loading | ok | invalid
    // {company, years, companies:[{bullstat, company_name, years:[{year, settlements}]}]}
    const [info, setInfo] = useState(null);
    // Ц1: един линк може да покрива няколко фирми (две фирми на един човек).
    const [bullstat, setBullstat] = useState(null);
    // Щ6: „Справки" или „Землища". Земищата са досегашният изглед и остават
    // подразбиращият се — линк без разрешени справки изглежда точно както преди.
    const [view, setView] = useState('settlements');
    const [reportKey, setReportKey] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [reportError, setReportError] = useState('');
    const [year, setYear] = useState(null);
    const [ekatte, setEkatte] = useState(null);
    const [report, setReport] = useState(null);
    const [pages, setPages] = useState(0);
    const [geometry, setGeometry] = useState([]);   // Т2: размери + решетка плочки
    // Ф3: кои землища ги е виждал вече на това устройство
    const [seen, setSeen] = useState(() => readSeen(token));
    // Ъ8: кои съобщения/справки с „Известяване" вече са видени на устройството.
    const [noticesSeen, setNoticesSeen] = useState(() => readNotices(token));
    // Ъ12: расте при всяко връщане към страницата — виж ефекта по-долу.
    const [поколение, setПоколение] = useState(0);

    const pub = async (path) => {
        const res = await fetch(`${baseUrl()}/public/${token}${path}`);
        if (!res.ok) throw new Error("not ok");
        return res.json();
    };

    useEffect(() => {
        document.title = "Справка";
    }, []);

    useEffect(() => {
        pub("")
            .then(data => {
                setInfo(data);
                setStatus("ok");
                const първа = data.companies && data.companies.length > 0
                    ? data.companies[0]
                    : {bullstat: data.company?.bullstat, years: data.years || []};
                setBullstat(първа.bullstat);
                if (първа.years && първа.years.length > 0) setYear(първа.years[0].year);
            })
            .catch(() => setStatus("invalid"));
    }, [token]);

    // Ъ12 (03.10.2026) — ВРЪЩАНЕ КЪМ СКРИТОТО ПРИЛОЖЕНИЕ.
    //
    // Плъзването настрани на телефона не затваря закачената икона, а я скрива.
    // Върне ли се човекът при нея, вижда това, което е било заредено преди —
    // ново съобщение или справка не личи, докато не затвори изрично и не пусне
    // наново. Затова при всяко показване на страницата данните се теглят пак.
    //
    // Само ТЕГЛЕНЕ: изборът на фирма, година и справка остава какъвто е бил, а
    // неуспешна заявка (спряла мрежа на полето) не пипа вече показаното.
    useEffect(() => {
        const пробуждане = () => {
            if (document.visibilityState === "visible") setПоколение(n => n + 1);
        };
        document.addEventListener("visibilitychange", пробуждане);
        // Връщане „назад" от кеша на браузъра не минава през visibilitychange.
        window.addEventListener("pageshow", пробуждане);
        return () => {
            document.removeEventListener("visibilitychange", пробуждане);
            window.removeEventListener("pageshow", пробуждане);
        };
    }, []);

    useEffect(() => {
        if (поколение === 0) return;          // първото зареждане е горе
        let отменено = false;
        pub("")
            .then(data => { if (!отменено) setInfo(data); })
            .catch(() => {});                  // мълчи: на екрана остава старото
        return () => { отменено = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [поколение, token]);

    // Смяна на година → нулиране на избора.
    useEffect(() => {
        setEkatte(null);
        setReport(null);
        setPages(0);
        setGeometry([]);
    }, [year]);

    // Щ6: справката се тегли при избор и се презарежда при смяна на година или
    // фирма. Без това превключването на фирма би оставило чуждите числа.
    //
    // Ъ5 (бъг от живо ползване): данните се пазят ЗАЕДНО с това, за какво са
    // заредени (справка + година + фирма), и се рисуват само ако съвпада с
    // текущия избор. Преди се пазеше само `reportData`: при клик от С11 на С14
    // ключът се сменяше веднага, а старите данни се чистеха чак в ефекта, СЛЕД
    // рисуването — за един кадър таблицата на С14 получаваше данните на С11,
    // търсеше колона, която ги няма, и цялата страница падаше в „Възникна
    // неочаквана грешка". Отвореше ли се направо С14, не личеше.
    const заявка = reportKey && year != null && bullstat
        ? `${reportKey}|${year}|${bullstat}` : null;
    useEffect(() => {
        if (!заявка) {
            setReportData(null);
            return;
        }
        let отменена = false;
        setReportError('');
        pub(`/reports/${reportKey}/${year}?bullstat=${bullstat}`)
            .then(d => { if (!отменена) setReportData({for: заявка, data: d}); })
            .catch(() => { if (!отменена) setReportError('Справката не се зареди.'); });
        // Бърз клик С11 → С14: закъснелият отговор на С11 не бива да затрие С14.
        return () => { отменена = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [заявка, token, поколение]);

    // Само данни, заредени ТОЧНО за текущия избор; иначе — нищо (показва се
    // „Зареждане…"), никога чужда справка в чужда таблица.
    const текущиДанни = reportData && reportData.for === заявка ? reportData.data : null;

    // Избор на землище → зареждане на справката + страниците на PDF-а.
    useEffect(() => {
        if (year == null || !ekatte) return;
        setReport(null);
        setPages(0);
        setGeometry([]);
        pub(`/report/${year}/${ekatte}${bullstat ? `?bullstat=${bullstat}` : ''}`)
            .then(setReport).catch(() => setReport(null));
        pub(`/doc/${year}/${ekatte}`)
            .then(d => { setPages(d.pages || 0); setGeometry(d.geometry || []); })
            .catch(() => { setPages(0); setGeometry([]); });
    }, [year, ekatte, bullstat]);

    // Първото отваряне на дадена година само запомня състоянието — без да
    // обявява нищо за ново. Стои в ефект, а не в рисуването: записът е
    // страничен ефект и при две рисувания би се изпълнил два пъти.
    useEffect(() => {
        if (!info || year == null) return;
        const списъкФирми = фирмиОт(info);
        const текуща = списъкФирми.find(c => String(c.bullstat) === String(bullstat))
            || списъкФирми[0];
        const yo = (текуща.years || []).find(y => y.year === year);
        const списък = yo ? yo.settlements : [];
        // Ц1: всяка фирма има свой списък землища, значи и своя отправна точка.
        const ключ = scopeKey(year, текуща.bullstat, списъкФирми[0].bullstat);
        if (baselineFor(readSeen(token), year, ключ) === undefined && списък.length > 0) {
            setSeen(writeSeen(token, year, списък, ключ));
        }
    }, [info, year, bullstat, token]);

    if (status === "loading") {
        return <div style={{maxWidth: "900px", margin: "40px auto", textAlign: "center"}}>Зареждане…</div>;
    }
    if (status === "invalid") {
        return (
            <div style={{maxWidth: "600px", margin: "60px auto", textAlign: "center"}}>
                <img src={logo3} alt="" style={{maxWidth: "320px", width: "100%", marginBottom: "24px"}}/>
                <h4 style={{color: "var(--app-gold)"}}>Линкът не е активен.</h4>
            </div>
        );
    }

    const companies = фирмиОт(info);
    const current = companies.find(c => String(c.bullstat) === String(bullstat)) || companies[0];
    const years = current.years || [];
    const yearObj = years.find(y => y.year === year);
    const settlements = yearObj ? yearObj.settlements : [];

    // Ц1: смяната на фирма нулира избора ръчно. Ако новата фирма работи в
    // същата година, `year` не се променя и ефектът по година не се задейства —
    // тогава на екрана би останала справката на предишната фирма.
    const смениФирма = (bs) => {
        const нова = companies.find(c => String(c.bullstat) === String(bs));
        if (!нова || String(нова.bullstat) === String(current.bullstat)) return;
        setBullstat(нова.bullstat);
        setEkatte(null);
        setReport(null);
        setPages(0);
        setGeometry([]);
        const години = нова.years || [];
        if (!години.some(y => y.year === year)) {
            setYear(години.length > 0 ? години[0].year : null);
        }
    };

    // Ф3: сравнение с последното отваряне. Ако тази година се отваря за първи
    // път на устройството, отправна точка няма — тогава нищо не е „ново" и
    // списъкът се запомня тихо (иначе при първото влизане светва всичко).
    const ключВидяно = scopeKey(year, current.bullstat, companies[0].bullstat);
    const базово = baselineFor(seen, year, ключВидяно);
    const промени = базово
        ? diffSettlements(базово, settlements)
        : {added: [], removed: []};
    const имаПромени = промени.added.length > 0 || промени.removed.length > 0;
    const нови = new Set(промени.added.map(s => s.ekatte));

    const отбележиВидяно = () => setSeen(writeSeen(token, year, settlements, ключВидяно));

    // Щ6: справките са на самия ЛИНК, не на фирмата — един и същ набор важи за
    // всички фирми зад него.
    const разрешениСправки = info.reports || [];
    const показваСправки = view === 'reports' && разрешениСправки.length > 0;

    // Ъ8: съобщенията са на линка, не на година — затова разделът не носи година.
    const съобщения = info.messages || [];
    const показваСъобщения = view === 'messages' && съобщения.length > 0;
    const показваЗемлища = !показваСправки && !показваСъобщения;
    const имаРаздели = (разрешениСправки.length > 0 && years.length > 0) || съобщения.length > 0;

    // Ъ8: лентата най-горе — само за нещата с отметка „Известяване", които това
    // устройство още не е видяло. „Разбрах" ги отбелязва; самите съобщения
    // остават в раздела „Съобщения".
    const известия = unseenNotices(noticesSeen, съобщения, разрешениСправки);
    const разбрах = () => setNoticesSeen(markNoticesSeen(token, известия.map(n => n.key)));
    const виж = (n) => {
        if (n.kind === 'message') {
            setView('messages');
        } else {
            setView('reports');
            setReportKey(n.item.key);
        }
        разбрах();
    };

    return (
        // Ъ13: докато условията не са приети на това устройство, се вижда само
        // краткият текст със „Съгласен съм".
        <ConsentGate token={token} companyName={companies.map(c => c.company_name).join(", ")}>
        <div style={{maxWidth: "1000px", margin: "0 auto", padding: "24px 16px"}}>
            <div className="d-flex flex-column align-items-center mb-3">
                <img src={logo3} alt="" style={{maxWidth: "360px", width: "100%"}}/>
                <h4 className="mt-2 text-center" style={{color: "var(--app-gold)"}}>{current.company_name}</h4>
                <div className="text-muted">Булстат: {current.bullstat}</div>
            </div>

            {/* Ъ8: известие за нови неща. Стои НАЙ-ГОРЕ, преди всичко друго — за
                да се види още при отварянето, без превъртане. */}
            {известия.length > 0 && (
                <div className="alert alert-warning mb-3">
                    <div className="fw-bold mb-1">
                        {известия.length === 1 ? "Имате ново:" : `Имате ${известия.length} нови неща:`}
                    </div>
                    {известия.map(n => (
                        <div key={n.key} className="d-flex justify-content-between align-items-center gap-2 mb-1">
                            {/* `flex: 1` + `minWidth: 0` + пренасяне навсякъде: иначе
                                дълга дума или адрес без интервали избутва бутона
                                „Виж" извън лентата (видяно при проверката). */}
                            <div style={{minWidth: 0, flex: 1, overflowWrap: "anywhere"}}>
                                {n.kind === 'message'
                                    ? <>✉️ {кратко(n.item.body)}</>
                                    : <>📊 Добавена е справка: <strong>{n.item.name}</strong></>}
                            </div>
                            <button className="btn btn-sm btn-outline-dark flex-shrink-0"
                                    onClick={() => виж(n)}>Виж</button>
                        </div>
                    ))}
                    <div className="text-end mt-2">
                        <button className="btn btn-sm btn-dark" onClick={разбрах}>Разбрах</button>
                    </div>
                </div>
            )}

            {/* Ц1: две фирми на един и същ човек — един линк, превключване тук.
                Бутони, а не падащ списък: на телефон се уцелват с пръст и се
                вижда наведнъж кои са фирмите зад този линк. */}
            {companies.length > 1 && (
                <div className="mb-3">
                    <div className="mb-1 text-muted">Фирма:</div>
                    <div className="d-flex gap-2 flex-wrap">
                        {companies.map(c => (
                            <button key={c.bullstat}
                                    className={`btn btn-sm ${String(c.bullstat) === String(current.bullstat)
                                        ? "btn-success" : "btn-outline-success"}`}
                                    onClick={() => смениФирма(c.bullstat)}>
                                {c.company_name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* С2: закачане на екрана + QR. Стои горе, защото е нещо, което се
                прави ВЕДНЪЖ, при първото отваряне. */}
            <SaveToDevice token={token} companyName={companies.map(c => c.company_name).join(", ")}/>

            {/* Ъ9: известие на телефона и знак върху иконата — след разрешение. */}
            <PushNotifications token={token}/>

            {years.length === 0 && (
                <div className="alert alert-info">Няма налични данни.</div>
            )}

            {years.length > 0 && (
                <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
                    <label className="mb-0">Стопанска година:</label>
                    <select className="form-select" style={{width: "auto"}}
                            value={year ?? ""} onChange={e => setYear(Number(e.target.value))}>
                        {years.map(y => <option key={y.year} value={y.year}>{y.year}</option>)}
                    </select>
                </div>
            )}

            {/* Щ6: два раздела — „Справки <година>" и „Землища". Показват се
                само ако за този линк са разрешени справки; иначе страницата е
                каквато си беше. */}
            {имаРаздели && (
                <ul className={`nav nav-tabs mb-3 ${styles.tabs}`}>
                    {разрешениСправки.length > 0 && years.length > 0 && (
                        <li className="nav-item">
                            <button className={`nav-link ${styles.tab} ${view === 'reports' ? `active ${styles.tabActive}` : ''}`}
                                    onClick={() => setView('reports')}>
                                Справки {year}
                            </button>
                        </li>
                    )}
                    <li className="nav-item">
                        <button className={`nav-link ${styles.tab} ${показваЗемлища ? `active ${styles.tabActive}` : ''}`}
                                onClick={() => setView('settlements')}>
                            Землища
                        </button>
                    </li>
                    {/* Ъ8: „Съобщения" — появява се само ако линкът има съобщения. */}
                    {съобщения.length > 0 && (
                        <li className="nav-item">
                            <button className={`nav-link ${styles.tab} ${показваСъобщения ? `active ${styles.tabActive}` : ''}`}
                                    onClick={() => setView('messages')}>
                                Съобщения ({съобщения.length})
                            </button>
                        </li>
                    )}
                </ul>
            )}

            {показваСъобщения && (
                <div className="mb-3">
                    {съобщения.map(m => (
                        <div key={m.id} className={`card mb-3 ${styles.message}`}>
                            <div className="card-body">
                                <div className="text-muted mb-2" style={{fontSize: "0.85rem"}}>{m.date}</div>
                                <div style={{whiteSpace: "pre-wrap"}}>{m.body}</div>
                                {m.has_image && (
                                    <a href={`${baseUrl()}/public/${token}/messages/${m.id}/image`}
                                       target="_blank" rel="noopener noreferrer">
                                        <img src={`${baseUrl()}/public/${token}/messages/${m.id}/image`}
                                             alt="" loading="lazy" className={`mt-2 ${styles.messageImage}`}/>
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {показваСправки && (
                <div className="mb-3">
                    <div className="d-flex gap-2 flex-wrap mb-2">
                        {разрешениСправки.map(r => (
                            <button key={r.key}
                                    className={`btn btn-sm ${reportKey === r.key
                                        ? "btn-warning" : "btn-outline-secondary"}`}
                                    onClick={() => setReportKey(reportKey === r.key ? null : r.key)}>
                                {r.name}
                            </button>
                        ))}
                    </div>
                    {!reportKey && (
                        <div className="text-muted">
                            Изберете справка. Данните са за {current.company_name},
                            стопанска {year} година.
                        </div>
                    )}
                    {reportKey && (
                        <div className="text-muted mb-2" style={{fontSize: "0.9rem"}}>
                            {разрешениСправки.find(r => r.key === reportKey)?.description}
                        </div>
                    )}
                    {reportKey && !текущиДанни && !reportError && <div>Зареждане…</div>}
                    {reportError && <div className="alert alert-warning">{reportError}</div>}
                    {текущиДанни && reportKey === 's5' && <CrossView data={текущиДанни} showGrandTotal={false}/>}
                    {текущиДанни && reportKey === 's11' && <CropsView data={текущиДанни}/>}
                    {текущиДанни && reportKey === 's14' && <S14View data={текущиДанни}/>}
                </div>
            )}

            {показваЗемлища && имаПромени && (
                <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        {промени.added.length > 0 && (
                            <div>
                                <strong>Ново от последното ви отваряне:</strong>{" "}
                                {промени.added.map(s => s.name).join(", ")}
                            </div>
                        )}
                        {промени.removed.length > 0 && (
                            <div>
                                <strong>Вече не фигурира:</strong>{" "}
                                {промени.removed.map(s => s.name).join(", ")}
                            </div>
                        )}
                    </div>
                    <button className="btn btn-sm btn-outline-dark" onClick={отбележиВидяно}>
                        Разбрах
                    </button>
                </div>
            )}

            {показваЗемлища && settlements.length > 0 && (
                <div className="mb-3">
                    <div className="mb-1 text-muted">Землища:</div>
                    <div className="d-flex gap-2 flex-wrap">
                        {settlements.map(s => (
                            <button key={s.ekatte}
                                    className={`btn btn-sm ${ekatte === s.ekatte ? "btn-warning" : "btn-outline-secondary"}`}
                                    onClick={() => setEkatte(s.ekatte)}>
                                {s.name}
                                {нови.has(s.ekatte) && (
                                    <span className="badge bg-warning text-dark ms-2">ново</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {показваЗемлища && ekatte && report && <S3View data={report}/>}

            {показваЗемлища && ekatte && pages > 0 && (
                <div className="mt-4">
                    <DocViewer
                        pages={pages}
                        geometry={geometry}
                        srcFor={n => `${baseUrl()}/public/${token}/doc/${year}/${ekatte}/page/${n}`}
                        tileSrcFor={(n, c, r) =>
                            `${baseUrl()}/public/${token}/doc/${year}/${ekatte}/page/${n}/tile/${c}/${r}`}/>
                </div>
            )}

            {показваЗемлища && ekatte && report && pages === 0 && (
                <div className="text-muted mt-3">Няма приложен документ за това землище.</div>
            )}
        </div>
        </ConsentGate>
    );
};
